/* eslint-disable no-useless-escape */

/* eslint-disable no-labels */
import { useCloseOnEsc } from '../../../../hooks/useCloseOnEsc';
import { useOutsideClick } from '../../../../hooks/useOutsideClick';
import Button from '../../../atoms/button';
import { Checkbox } from '../../../atoms/checkbox/Checkbox';
import { TickBlackFilled } from '../../../atoms/icons';
import { TextField } from '../../../atoms/textField/TextField';
import { Heading } from '../../../typography/heading/Heading';
import { Text } from '../../../typography/text/Text';
import { createVIPAccountSchema } from '../utils/validationSchemas';
import { useCustomScrollToTop } from '../../../../utils/useCustomScrollToTop';
import styles from './CreateAccount.module.scss';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMedia } from 'react-use';
import * as yup from 'yup';
import YupPassword from 'yup-password';
import { useNostoContext } from '../../../../provider/nosto/NostoContext';
import { theme } from './_vars';
import { useCsrfToken } from '../../../../utils/useCsrfToken';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import PrimaryLoader from '../../../atoms/primaryLoader/PrimaryLoader';
import Notification from '../../../atoms/notification';
import PasswordField from '../../../atoms/passwordField';
import { getCurrentLocale } from '../../../../utils/getCurrentLocale';
import { useGlobalSettings } from '../../../../utils/useGlobalSettings';
import EmailField from '../../../atoms/emailField/EmailField';
import { useYupValidationResolver } from '../../../../utils/useYupValidationResolver';

YupPassword(yup);

const CreateAccount = ({
  onClose = () => null,
  onCreate = () => null,
  goToLoginAction,
  isSuccess,
  email,
  placement = 'default',
  benefits,
  title,
  subtitle,
  defaultValues = {
    email: '',
    firstName: '',
    lastName: '',
    password: '',
  },
  scrollToTop,
  isPopup,
}) => {
  const isDesktop = useMedia('(min-width: 1025px)', true);
  const [loading, setLoading] = useState(false);
  const [emailIsReady, setEmailIsReady] = useState(false);
  const [customEmailError, setCustomEmailError] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [isSingedToNewsletterEmail, setIsSingedToNewsletterEmail] = useState(false);
  const { nostoUpdateCustomer } = useNostoContext();
  const currentSiteRegion = getCurrentLocale();
  const { vipDiscount } = useGlobalSettings({ locale: currentSiteRegion });

  const defaultEmail = isSingedToNewsletterEmail || window?.sessionStorage.getItem('newsLetterEmail') || email;

  const resolver = useYupValidationResolver(createVIPAccountSchema);

  const { register, handleSubmit, formState, setError, setValue, control } = useForm({
    resolver,
    mode: 'all',
  });

  const { errors } = formState;

  useEffect(() => {
    const emailVal = window?.sessionStorage.getItem('newsLetterEmail');
    setIsSingedToNewsletterEmail(!!emailVal || false);
    if (emailVal) {
      setValue('email', emailVal);
    }
  }, []);

  const { csrfTokenComponent } = useCsrfToken('register', register, setValue);

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const containerRef = useRef(null);

  const { goToTop } = useCustomScrollToTop();

  useEffect(() => {
    scrollToTop && goToTop();
  }, []);

  useCloseOnEsc(isDesktop ? { onClose } : {});

  useOutsideClick({
    ref: containerRef,
    handler: isDesktop ? onClose : null,
  });

  const handleReCaptchaVerify = React.useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }
    const recTokenResult = await executeRecaptcha('create_account_form');
    setRecaptchaToken(recTokenResult);
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [executeRecaptcha]);

  const handleSubmitError = (errorMessage) => {
    setLoading(false);
    // this crutch is needed to display error about the user's incorrect email, in German - because the error comes in German from the frontastic. So it is necessary.
    if (
      errorMessage.includes('Die E-Mail-Adresse wird bereits verwendet') ||
      errorMessage.includes('This e-mail-address is already in use.')
    ) {
      setCustomEmailError('Email is already in use.');
    }

    if (errorMessage.includes('reCaptcha')) {
      setErrorMessage(errorMessage);
    }
    handleReCaptchaVerify();
  };

  const onSubmit = useCallback(
    async (data) => {
      setLoading(true);
      const formatData = {
        ...data,
        recaptcha_token: recaptchaToken,
      };

      nostoUpdateCustomer({
        email: formatData?.email,
        firstName: formatData?.firstName,
        lastName: formatData?.lastName,
        marketingComms: formatData?.marketingComms,
      });
      const res = await onCreate(formatData, true, true, handleSubmitError);
      setLoading(false);
      if (res && res.loggedIn) {
        onClose();
      }
    },
    [recaptchaToken],
  );

  const isCreateAccountButtonDisabled =
    !emailIsReady || !formState.isDirty || !formState.isValid || loading || customEmailError;

  return (
    <div
      ref={containerRef}
      className={clsx(styles.createAccountContainer, styles[`createAccountContainer_${placement}`])}
    >
      <Heading className={styles.heading} textTransform="firstletter" as="h4" weight="light">
        {title || 'Create a VIP account'}
      </Heading>

      <Text className={styles.subtitle} as="p" size="xs">
        {subtitle || `VIP members enjoy ${vipDiscount}% off full price items`}
      </Text>
      {isSingedToNewsletterEmail && (
        <Text className={`${styles.singupCallout} ${styles.subtitleRed}`} as="p">
          {`You’re signed up to our mailing list, but to receive ${vipDiscount}% off please
          create an account below.`}
        </Text>
      )}
      {benefits && benefits.length > 0 && (
        <ul type="square" className={styles.listContainer}>
          {benefits.map((item, index) => {
            return (
              <li key={index}>
                <Text className={styles.listItem} as="p" size={theme === 'Jag' ? 'sm' : 'xs'}>
                  <TickBlackFilled />
                  {item.text}
                </Text>
              </li>
            );
          })}
        </ul>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        {csrfTokenComponent}
        <input
          type="hidden"
          {...register('recaptcha_token')}
          defaultValue={recaptchaToken}
          className={styles.borderNone}
        />
        <TextField
          className={clsx(styles.textField, {
            [styles.hidden]: isSuccess,
          })}
          type="text"
          defaultValue={defaultValues?.firstName && defaultValues?.firstName}
          placeholder="First Name"
          variant="underlined"
          {...register('firstName')}
          errorMessage={errors?.firstName?.message}
          error={!!errors?.firstName}
        />
        <TextField
          className={clsx(styles.textField, {
            [styles.hidden]: isSuccess,
          })}
          defaultValue={defaultValues?.lastName && defaultValues?.lastName}
          placeholder="Last Name"
          variant="underlined"
          {...register('lastName')}
          errorMessage={errors?.lastName?.message}
          error={!!errors?.lastName}
        />
        <TextField
          className={styles.textField}
          type="tel"
          placeholder="Mobile Number (optional)"
          variant="underlined"
          {...register('phone')}
          maxLength={14}
          inputMode="tel"
          errorMessage={errors?.phone?.message}
          error={!!errors?.phone}
        />
        <EmailField
          className={styles.textField}
          placeholder="Email Address"
          variant="underlined"
          name="email"
          defaultValue={defaultEmail}
          register={register}
          setError={setError}
          setValue={setValue}
          onKeyDown={() => setCustomEmailError('')}
          setEmailIsReady={setEmailIsReady}
          errorMessage={customEmailError ? customEmailError : errors?.email?.message || errors?.email?.types?.message}
          error={!!customEmailError || !!errors?.email || !!errors?.email?.types?.message}
        />
        <PasswordField
          placement={placement}
          inputStyle={styles.textField}
          noteStyle={styles.note}
          placeholder="Create Password"
          variant="underlined"
          name="password"
          register={register}
          errorMessage={!!errors?.password?.message}
          error={!!errors?.password}
          isPopup={isPopup}
        />
        <Controller
          name="marketingComms"
          control={control}
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <Checkbox
              className={styles.checkboxField}
              variant="inverse"
              label={`I would like to receive marketing comms. I agree to be a VIP in ${theme}'s VIP programme & agree to ${theme}'s T&C's and how my data may be collected & used as setout in ${theme}'s Privacy Policy and Terms and Conditions`}
              onBlur={onBlur}
              checked={value}
              ref={ref}
              onChange={(value) => {
                setValue('marketingComms', value);
                onChange(value);
              }}
              required
            />
          )}
        />
        {errorMessage && (
          <div className={styles.errorBlock}>
            <Notification type="error" message={errorMessage} />
          </div>
        )}
        <Button
          disabled={isCreateAccountButtonDisabled}
          className={clsx(styles.btnLogin, { [styles.btnLoginDisabled]: isCreateAccountButtonDisabled })}
          type="submit"
        >
          {loading ? <PrimaryLoader theme="light" mode="segment" size={22} /> : 'Create an account'}
        </Button>
      </form>
      {!isSuccess && (
        <div className={styles.footerContainer}>
          <Text className={styles.text} align="center" as="p">
            Already have an Account?
          </Text>
          <Button className={styles.link} as="text" onClick={goToLoginAction}>
            Sign In
          </Button>
        </div>
      )}
    </div>
  );
};

CreateAccount.propTypes = {
  onClose: PropTypes.func,
  onCreate: PropTypes.func,
  defaultValues: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    password: PropTypes.string,
  }),
};

export default CreateAccount;
